/**
 * SuccessStage — terminal "Complete" stage. DISPLAY ONLY.
 *
 * Delivery already happened per-output in the preceding Deliver stages, and the BACKEND
 * finalizes the document status at the last output submit (or the scheduled batch exporter
 * does, for queued outputs) — the browser never advances document state (UX3 fire-and-forget).
 * This stage just shows the recorded outcome, then advances to the next document of the
 * same model. If the outcome can't be fetched it says so — it never asserts success it
 * hasn't confirmed.
 *
 * When `sourceGroup` is set (analyst came from the exception dashboard), "Next Document →"
 * fetches the next item from that same exception group (failed / stranded / on-hold) so the
 * admin can work through the entire group without bouncing back to the dashboard. When the
 * group is exhausted it navigates back to the exception dashboard.
 */

import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDocumentService } from '../../../services/documentService';
import { apiService, DocumentResponse } from '../../../services/apiService';
import OutputStatusList from '../../shared/OutputStatusList';

/** Human labels for the server-recorded status — clerks never see raw enums. */
const STATUS_LABELS: Record<string, string> = {
  COMPLETED: 'Completed',
  SUBMISSION_PENDING: 'Awaiting scheduled export',
  SUBMITTED: 'Delivered',
  VALIDATED: 'Validated',
  PROCESSED: 'In review',
  FAILED: 'Delivery failed',
};

interface SuccessStageProps {
  documentId: string;
  /** Keeps the analyst on the same document model when fetching the next queued document. */
  documentType?: string;
  onNextDocument?: (nextDocumentId: string) => void;
  /** When set, "Next Document" stays within this exception group rather than the general queue. */
  sourceGroup?: 'stranded' | 'failed' | 'onHold';
}

export default function SuccessStage({ documentId, documentType, onNextDocument, sourceGroup }: SuccessStageProps) {
  const { getNextProcessedDocument } = useDocumentService();
  const [loading, setLoading] = useState(false);
  const [outcomeLoaded, setOutcomeLoaded] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [batchPending, setBatchPending] = useState(false);
  const [documentStatus, setDocumentStatus] = useState<string | null>(null);
  const [submission, setSubmission] = useState<DocumentResponse['submission'] | null>(null);

  // Read the outcome the backend recorded: the per-output submission rows plus the final
  // status set server-side (COMPLETED, or SUBMISSION_PENDING while batch outputs await
  // their scheduled export).
  useEffect(() => {
    let cancelled = false;

    const loadOutcome = async () => {
      try {
        const doc: DocumentResponse = await apiService.getDocument(documentId);
        if (cancelled) return;
        setSubmission(doc.submission ?? null);
        setBatchPending(((doc.submission as any)?.pendingOutputs?.length ?? 0) > 0);
        setDocumentStatus(doc.currentStatus ?? null);
      } catch (err) {
        console.warn('[SuccessStage] Failed to fetch submission status:', err);
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setOutcomeLoaded(true);
      }
    };

    loadOutcome();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  /** Pick the next document from the same exception group the analyst was working through.
   *  Returns null when the group is empty (all resolved). */
  const getNextFromExceptionGroup = async (group: 'stranded' | 'failed' | 'onHold'): Promise<string | null> => {
    const result = await apiService.getExceptionDashboard();
    const data = result?.data;
    if (!data) return null;

    if (group === 'failed') {
      const next = (data.failed as any[])?.[0];
      return next?.documentId ?? null;
    }
    if (group === 'onHold') {
      const next = (data.onHold as any[])?.[0];
      return next?.documentId ?? null;
    }
    if (group === 'stranded') {
      // Stranded has one row per (doc, output) — de-duplicate to get distinct docs.
      const stranded: any[] = data.stranded ?? [];
      const seen = new Set<string>();
      for (const row of stranded) {
        if (row.documentId && row.documentId !== documentId && !seen.has(row.documentId)) {
          return row.documentId;
        }
        if (row.documentId) seen.add(row.documentId);
      }
      return null;
    }
    return null;
  };

  const handleNextDocument = async () => {
    setLoading(true);

    try {
      let nextDocId: string | null = null;

      if (sourceGroup) {
        // Stay within the exception group the admin came from.
        nextDocId = await getNextFromExceptionGroup(sourceGroup);
        if (!nextDocId) {
          // Group is fully processed — go back to the exception dashboard.
          window.location.href = '/dashboard';
          return;
        }
      } else {
        // Normal queue flow — next document of the same model in PENDING_VALIDATION.
        const nextDoc = await getNextProcessedDocument(documentType);
        nextDocId = nextDoc?.documentId ?? null;
      }

      if (nextDocId) {
        if (onNextDocument) {
          // Thread sourceGroup through so the analyst stays in the same group for the next doc.
          onNextDocument(nextDocId);
          setLoading(false);
        } else {
          window.location.href = `/documents/${nextDocId}`;
        }
      } else {
        // No more documents — return to the appropriate home.
        window.location.href = '/';
      }
    } catch (err) {
      console.warn('[SuccessStage] Failed to fetch next document, redirecting:', err);
      window.location.href = sourceGroup ? '/dashboard' : '/';
    }
  };

  // Ctrl+. — keyboard path to the next document (UX4, documented in the shortcut overlay).
  // Routed through a ref so the listener doesn't re-attach as loading state changes.
  const nextDocRef = useRef<() => void>(() => {});
  useEffect(() => {
    nextDocRef.current = () => {
      if (!loading) handleNextDocument();
    };
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '.' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        nextDocRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Don't assert an outcome before it's confirmed — show a small confirming state instead.
  if (!outcomeLoaded) {
    return (
      <Paper
        elevation={1}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Confirming delivery status…
        </Typography>
      </Paper>
    );
  }

  const nextButtonLabel = sourceGroup
    ? 'Next Exception →'
    : 'Next Document →';

  const exhaustedLabel = sourceGroup
    ? 'Back to Exception Dashboard'
    : 'Back to Queue';

  return (
    <Paper
      elevation={1}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}
    >
      {fetchError ? (
        <>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Review complete
          </Typography>
          <Alert severity="warning" sx={{ mt: 1, width: '100%', maxWidth: 440 }}>
            Couldn't confirm the final delivery status for document #{documentId}. Delivery
            continues on the server — check the Home queue for its current state.
          </Alert>
        </>
      ) : (
        <>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {batchPending ? 'Queued for delivery' : 'Complete'}
          </Typography>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 1 }}>
            Document #{documentId} {batchPending ? 'is queued for scheduled batch export' : 'processed successfully'}
          </Typography>

          {documentStatus && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Delivery status: {STATUS_LABELS[documentStatus] ?? documentStatus.replace(/_/g, ' ').toLowerCase()}
            </Typography>
          )}

          {submission && (
            <Box sx={{ mt: 3, width: '100%', maxWidth: 400 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Submission Status
              </Typography>
              <OutputStatusList
                outputs={submission.outputs}
                legacy={submission.augmentumResponse}
                submittedAt={submission.submittedAt}
              />
            </Box>
          )}
        </>
      )}

      <Button variant="contained" onClick={handleNextDocument} disabled={loading} sx={{ mt: 4 }}>
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Loading...
          </>
        ) : (
          nextButtonLabel
        )}
      </Button>
      {sourceGroup && (
        <Button
          variant="text"
          size="small"
          onClick={() => { window.location.href = '/dashboard'; }}
          sx={{ mt: 1 }}
          disabled={loading}
        >
          {exhaustedLabel}
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Shortcut: Ctrl+.
      </Typography>
    </Paper>
  );
}
